import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { serviceCategories, serviceImages, businessInfo } from '../data/mock';
import useReveal from '../hooks/useReveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatPrice = (service) => {
  if (service?.variable_pricing) return 'Consultation Required';
  if (service?.price === 0) return 'Complimentary';
  return `$${service?.price}`;
};

/**
 * ServiceCard — extracted so each card can own its own reveal observer
 * with a staggered delay derived from its index.
 */
const ServiceCard = ({ service, serviceId, idx, active }) => {
  const { ref, visible } = useReveal();
  const price = formatPrice(service);
  const isConsult = service.variable_pricing || service.price === 0;

  return (
    <article
      ref={ref}
      data-testid={`service-card-${serviceId}`}
      className={`reveal ${visible ? 'is-visible' : ''} group relative border border-pewter/70 bg-carbon/60 overflow-hidden hover:border-blush/70 transition-[border-color,transform,box-shadow] duration-500 hover:-translate-y-[2px] hover:shadow-[0_24px_60px_-30px_rgba(0,0,0,0.85)]`}
      style={{ '--reveal-delay': `${Math.min(idx, 3) * 100}ms` }}
    >
      {/* Service image */}
      <div className="relative w-full aspect-[16/9] overflow-hidden border-b border-pewter/60">
        <img
          src={serviceImages[serviceId]}
          alt={service.name}
          className="w-full h-full object-cover editorial-img group-hover:scale-[1.02] transition-transform duration-700"
          loading="lazy"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,10,11,0.1) 40%, rgba(10,10,11,0.7) 100%)',
          }}
        />
        <span className="absolute bottom-4 left-5 text-[10px] tracking-[0.28em] uppercase chrome-rose">
          {active.eyebrow}
        </span>
      </div>

      <div className="p-7 sm:p-8 md:p-10">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-6">
          <p className="eyebrow leading-snug">
            {String(idx + 1).padStart(2, '0')} · {service.subtitle || active.eyebrow}
          </p>
          <span className="text-silverx text-xs tracking-[0.2em] uppercase sm:text-right whitespace-nowrap">
            {service.duration} min
          </span>
        </div>

        <h4 className="font-serif text-ivory text-2xl md:text-[28px] leading-tight mb-4 text-balance">
          {service.name}
        </h4>
        <p className="text-platinum text-sm md:text-base leading-relaxed mb-8 max-w-md">
          {service.description}
        </p>

        <div className="hairline mb-6" />

        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex flex-col gap-1.5">
            <p
              className={`font-serif ${
                isConsult
                  ? 'brand-italic chrome-rose text-lg md:text-xl'
                  : 'text-ivory text-xl md:text-2xl'
              }`}
              data-testid={`service-price-${serviceId}`}
            >
              {price}
            </p>
            {!isConsult && service.price > 0 && (
              <p className="text-silverx text-[10px] tracking-[0.22em] uppercase">
                · per session
              </p>
            )}
          </div>
          <a
            href={businessInfo.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`service-book-${serviceId}`}
            className="btn-pill inline-flex items-center gap-2 px-5 py-3 rounded-full border border-pewter text-ivory text-[11px] tracking-[0.24em] uppercase hover:bg-ivory hover:text-onyx hover:border-ivory"
          >
            {isConsult ? 'Book Consult' : 'Reserve'}
            <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </article>
  );
};

const Services = () => {
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(serviceCategories[0].id);

  // Reveal hooks for header + bespoke CTA
  const headerReveal = useReveal();
  const bespokeReveal = useReveal();

  useEffect(() => {
    axios
      .get(`${API}/services`)
      .then((res) => {
        setServices(res.data.services || {});
        setLoading(false);
      })
      .catch((err) => {
        console.error('Services fetch failed', err);
        setLoading(false);
      });
  }, []);

  const active = serviceCategories.find((c) => c.id === activeCategory);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-32 md:py-36 bg-onyx overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header */}
        <div
          ref={headerReveal.ref}
          className={`reveal ${headerReveal.visible ? 'is-visible' : ''} flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16`}
        >
          <div className="max-w-2xl">
            <p className="eyebrow mb-6">
              <span className="inline-block w-8 h-px bg-silverx align-middle mr-3" />
              The Menu
            </p>
            <h2 className="font-serif text-ivory text-4xl sm:text-5xl lg:text-[60px] leading-[1.05] text-balance">
              Curated Signature{' '}
              <span className="brand-italic chrome-rose">Treatments</span>
            </h2>
          </div>
          <p className="text-platinum text-base md:text-lg leading-relaxed max-w-md">
            Luxury Sculpting · Wellness · Aesthetic Enhancement. Every ritual is
            tailored to your goals—strategic, intentional, and always private.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="relative mb-12" data-testid="services-tabs">
          <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1 no-scrollbar snap-x snap-mandatory">
            {serviceCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`category-tab-${cat.id}`}
                className={`snap-start flex-shrink-0 whitespace-nowrap px-5 py-3 rounded-full border text-[11px] tracking-[0.16em] md:tracking-[0.24em] uppercase transition-all duration-300 ${
                  activeCategory === cat.id
                    ? 'bg-ivory text-onyx border-ivory'
                    : 'border-pewter text-platinum hover:border-blush hover:text-blush'
                }`}
              >
                {/* Short label on mobile, full eyebrow on md+ */}
                <span className="md:hidden">{cat.shortLabel || cat.eyebrow}</span>
                <span className="hidden md:inline">{cat.eyebrow}</span>
              </button>
            ))}
          </div>
          {/* Right-edge scroll affordance — fade gradient hinting more pills */}
          <div
            aria-hidden="true"
            className="md:hidden pointer-events-none absolute top-0 right-0 h-[calc(100%-1rem)] w-12"
            style={{
              background:
                'linear-gradient(to left, rgba(10,10,11,1) 0%, rgba(10,10,11,0) 100%)',
            }}
          />
          <div className="hairline mt-4" />
        </div>

        {/* Active category heading */}
        <div className="mb-10 max-w-2xl">
          <h3 className="font-serif text-ivory text-2xl md:text-3xl mb-3">
            {active.title}
          </h3>
          <p className="text-silverx text-sm md:text-base leading-relaxed">
            {active.description}
          </p>
        </div>

        {/* Services Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-silverx">
            <Loader2 className="animate-spin mr-3" size={18} /> Curating menu…
          </div>
        ) : (
          <div
            className={
              active.services.length === 1
                ? 'flex justify-center'
                : 'grid md:grid-cols-2 gap-6 md:gap-8'
            }
          >
            {active.services.map((serviceId, idx) => {
              const service = services[serviceId];
              if (!service) return null;
              return (
                <div
                  key={serviceId}
                  className={active.services.length === 1 ? 'w-full max-w-xl' : ''}
                >
                  <ServiceCard
                    service={service}
                    serviceId={serviceId}
                    idx={idx}
                    active={active}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* CTA Banner */}
        <div
          ref={bespokeReveal.ref}
          className={`reveal ${bespokeReveal.visible ? 'is-visible' : ''} mt-20 bg-carbon border border-pewter/70 p-10 md:p-14 flex flex-col lg:flex-row items-start lg:items-center lg:justify-between gap-8`}
        >
          <div className="max-w-xl">
            <p className="eyebrow mb-3">Bespoke Packages</p>
            <h3 className="font-serif text-ivory text-2xl md:text-3xl leading-tight text-balance">
              Craft a multi-session protocol tailored to your transformation.
            </h3>
            <p className="mt-4 text-silverx text-[11px] tracking-[0.22em] uppercase">
              Custom protocols · pricing curated by goals
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <a
              href={`tel:${businessInfo.phone}`}
              className="btn-pill inline-flex justify-center items-center px-7 py-4 rounded-full border border-pewter text-ivory text-[11px] tracking-[0.24em] uppercase whitespace-nowrap hover:border-blush hover:text-blush"
              data-testid="packages-call"
            >
              Call the Studio
            </a>
            <a
              href={businessInfo.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pill inline-flex justify-center items-center px-7 py-4 rounded-full bg-ivory text-onyx text-[11px] tracking-[0.24em] uppercase whitespace-nowrap hover:bg-blush"
              data-testid="packages-book"
            >
              Book Consultation
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
